import { jsonp } from "vue-jsonp";
import { transformGcj02ToShcrood } from "@/mixins/map/about-map-common";
import { getStorage, setStorage, removeStorage } from "@/utils/storage";

const L = window.L;
export const shAreaIds = [
  "310113",
  "310230",
  "310120",
  "310114",
  "310116",
  "310115",
  "310118",
  "310117",
  "310112",
  "310181",
  "310182",
  "310183",
];

export const SHEPSG4326CRS = new L.Proj.CRS(
  "EPSG:4326",
  "+proj=longlat +datum=WGS84 +no_defs ",
  {
    resolutions: [
      0.0013732910156250004, // Level 0
      6.866455078125002e-4, // Level 1
      3.433227539062501e-4, // Level 2
      1.7166137695312505e-4, // Level 3
      8.583068847656253e-5, // Level 4
      4.2915344238281264e-5, // Level 5
      2.1457672119140632e-5, // Level 6
      1.0728836059570316e-5, // Level 7
      5.364418029785158e-6, // Level 8
      2.682209014892579e-6, // Level 9
      1.34110450744629e-6, // Level 10
    ],
    origin: [-180, 90], // 切片原点
  }
);

// 本地开发走公司后端代理，线上直连政府真实地址
const shWeixingTile = (() => {
  return "https://mapserver.nyncw.sh.gov.cn/OneMapServer/rest/services/202301sat_dom_tdt/MapServer";
})();

// 上海天地图 499 持久化标记 key 与过期时长（1 小时）
export const SH_499_KEY = "shTianditu499Ts";
export const SH_499_EXPIRE = 3600000;

/**
 * @description: 读取 1 小时内的 499 降级状态，过期或不存在返回 null
 * @return: {ts, offsetLat, offsetLng} | null
 */
export function getSh499FallbackState() {
  const state = getStorage(SH_499_KEY);
  if (state && state.ts && Date.now() - state.ts < SH_499_EXPIRE) return state;
  if (state) removeStorage(SH_499_KEY);
  return null;
}

/**
 * @description: 持久化 499 标记与偏移量
 */
export function saveSh499FallbackState(offsetLat, offsetLng) {
  setStorage(SH_499_KEY, { ts: Date.now(), offsetLat, offsetLng });
}

/**
 * @description: 创建上海坐标系下的天地图和备用卫星底图
 * @param {string} shGisToken 上海天地图 token
 * @return: {shTianditu, shSatelliteMap, initialLayer, setFallback}
 */
export const createShanghaiMapLayers = (shGisToken) => {
  const shReprojectedSatelliteUrl =
    "https://map.snkoudai.com/maps/vt/lyrs=s&hl=zh-CN&gl=cn&scale=1&x={x}&y={y}&z={z}";
  const shReprojectedTileSize = 256;
  const shReprojectedR = 6378137;
  const shReprojectedOriginShift = Math.PI * shReprojectedR;
  const ShReprojectedSatelliteLayer = L.GridLayer.extend({
    createTile: function (coords, done) {
      const canvas = L.DomUtil.create("canvas", "leaflet-tile");
      canvas.width = shReprojectedTileSize;
      canvas.height = shReprojectedTileSize;
      const ctx = canvas.getContext("2d");
      // SHEPSG4326CRS 的 level 0 对应 EPSG:4326 的 zoom 10，level + 10 即为 Mercator 切片 zoom
      const z4326 = Math.round(coords.z) + 10;
      const tileWidthDeg = 360 / Math.pow(2, z4326);
      const tileHeightDeg = 180 / Math.pow(2, z4326 - 1);
      const offsetLat = this.options.offsetLat || 0;
      const offsetLng = this.options.offsetLng || 0;
      // 当前 tile 在上海偏移坐标系下的经纬度范围
      const minLonSh = -180 + coords.x * tileWidthDeg;
      const maxLonSh = minLonSh + tileWidthDeg;
      const maxLatSh = 90 - coords.y * tileHeightDeg;
      const minLatSh = maxLatSh - tileHeightDeg;
      // 卫星切片为 GCJ-02，转成 GCJ-02 范围去请求
      const minLon = minLonSh - offsetLng;
      const maxLon = maxLonSh - offsetLng;
      const maxLat = maxLatSh - offsetLat;
      const minLat = minLatSh - offsetLat;
      // 赤道分辨率与 EPSG:4326 层级一致
      const z3857 = z4326;
      const mercTileSizeMeters =
        (2 * shReprojectedOriginShift) / Math.pow(2, z3857);
      const lonToMercX = (lon) => (lon * shReprojectedOriginShift) / 180;
      const latToMercY = (lat) =>
        shReprojectedR * Math.log(Math.tan(((90 + lat) * Math.PI) / 360));
      const minX = lonToMercX(minLon);
      const maxX = lonToMercX(maxLon);
      const minY = latToMercY(minLat);
      const maxY = latToMercY(maxLat);
      const minTileX = Math.floor(
        (minX + shReprojectedOriginShift) / mercTileSizeMeters
      );
      const maxTileX = Math.floor(
        (maxX + shReprojectedOriginShift) / mercTileSizeMeters
      );
      const minTileY = Math.floor(
        (shReprojectedOriginShift - maxY) / mercTileSizeMeters
      );
      const maxTileY = Math.floor(
        (shReprojectedOriginShift - minY) / mercTileSizeMeters
      );
      const tilesToLoad = [];
      for (let x = minTileX; x <= maxTileX; x++) {
        for (let y = minTileY; y <= maxTileY; y++) {
          tilesToLoad.push({
            url: L.Util.template(shReprojectedSatelliteUrl, {
              x: x,
              y: y,
              z: z3857,
            }),
            x: x,
            y: y,
            z: z3857,
          });
        }
      }
      if (tilesToLoad.length === 0) {
        done(null, canvas);
        return canvas;
      }
      let loadedCount = 0;
      let hasError = false;
      tilesToLoad.forEach((item) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = function () {
          try {
            this._drawMercatorTile(
              ctx,
              img,
              item,
              minLon,
              minLat,
              maxLon,
              maxLat,
              shReprojectedTileSize
            );
          } catch (err) {
            console.warn("绘制 Mercator 切片失败", err, item.url);
            hasError = true;
          }
          loadedCount++;
          if (loadedCount === tilesToLoad.length) {
            done(hasError ? new Error("Tile loading failed") : null, canvas);
          }
        }.bind(this);
        img.onerror = function () {
          hasError = true;
          loadedCount++;
          if (loadedCount === tilesToLoad.length) {
            done(null, canvas);
          }
        };
        img.src = item.url;
      });
      return canvas;
    },
    _getMercatorTileBounds: function (x, y, z) {
      const mercTileSizeMeters =
        (2 * shReprojectedOriginShift) / Math.pow(2, z);
      const minX = x * mercTileSizeMeters - shReprojectedOriginShift;
      const maxX = (x + 1) * mercTileSizeMeters - shReprojectedOriginShift;
      const maxY = shReprojectedOriginShift - y * mercTileSizeMeters;
      const minY = shReprojectedOriginShift - (y + 1) * mercTileSizeMeters;
      const mercXToLon = (x) => (x * 180) / shReprojectedOriginShift;
      const mercYToLat = (y) =>
        (360 / Math.PI) * Math.atan(Math.exp(y / shReprojectedR)) - 90;
      return {
        minLon: mercXToLon(minX),
        maxLon: mercXToLon(maxX),
        minLat: mercYToLat(minY),
        maxLat: mercYToLat(maxY),
      };
    },
    _drawMercatorTile: function (
      ctx,
      img,
      t,
      dstMinLon,
      dstMinLat,
      dstMaxLon,
      dstMaxLat,
      dstSize
    ) {
      const mercBounds = this._getMercatorTileBounds(t.x, t.y, t.z);
      const overlapMinLon = Math.max(mercBounds.minLon, dstMinLon);
      const overlapMaxLon = Math.min(mercBounds.maxLon, dstMaxLon);
      const overlapMinLat = Math.max(mercBounds.minLat, dstMinLat);
      const overlapMaxLat = Math.min(mercBounds.maxLat, dstMaxLat);
      if (overlapMinLon >= overlapMaxLon || overlapMinLat >= overlapMaxLat)
        return;
      const mercYAtLat = (lat) =>
        shReprojectedR * Math.log(Math.tan(((90 + lat) * Math.PI) / 360));
      const mercYTop = mercYAtLat(mercBounds.maxLat);
      const mercYBottom = mercYAtLat(mercBounds.minLat);
      const mercYRange = mercYTop - mercYBottom;
      if (mercYRange <= 0) return;
      // 分条带绘制，缓解 Mercator 与地理坐标在纬度方向的非线性差异
      const strips = 8;
      for (let i = 0; i < strips; i++) {
        const stripMinLat =
          overlapMinLat + ((overlapMaxLat - overlapMinLat) * i) / strips;
        const stripMaxLat =
          overlapMinLat + ((overlapMaxLat - overlapMinLat) * (i + 1)) / strips;
        const srcTop =
          ((mercYTop - mercYAtLat(stripMaxLat)) / mercYRange) * img.height;
        const srcBottom =
          ((mercYTop - mercYAtLat(stripMinLat)) / mercYRange) * img.height;
        const dstTop =
          ((dstMaxLat - stripMaxLat) / (dstMaxLat - dstMinLat)) * dstSize;
        const dstBottom =
          ((dstMaxLat - stripMinLat) / (dstMaxLat - dstMinLat)) * dstSize;
        const srcLeft =
          ((overlapMinLon - mercBounds.minLon) /
            (mercBounds.maxLon - mercBounds.minLon)) *
          img.width;
        const srcRight =
          ((overlapMaxLon - mercBounds.minLon) /
            (mercBounds.maxLon - mercBounds.minLon)) *
          img.width;
        const dstLeft =
          ((overlapMinLon - dstMinLon) / (dstMaxLon - dstMinLon)) * dstSize;
        const dstRight =
          ((overlapMaxLon - dstMinLon) / (dstMaxLon - dstMinLon)) * dstSize;
        if (srcBottom > srcTop && dstBottom > dstTop) {
          ctx.drawImage(
            img,
            srcLeft,
            srcTop,
            srcRight - srcLeft,
            srcBottom - srcTop,
            dstLeft,
            dstTop,
            dstRight - dstLeft,
            dstBottom - dstTop
          );
        }
      }
    },
  });

  function shTiandituLayer(token, options) {
    options = options || {};
    var reg = /^http(s)?:\/\/(.*?)\//;
    let origin = reg.exec(shWeixingTile)[0];
    if (!origin) return;
    const CustomTileLayer = L.TileLayer.extend({
      createTile: function (coords, done) {
        const tile = L.DomUtil.create("img", "leaflet-tile");
        const tileSize = this.getTileSize();
        tile.style.width = tileSize.x + "px";
        tile.style.height = tileSize.y + "px";
        tile.crossOrigin = "anonymous";
        const url = this.getTileUrl(coords);
        // 设置超时时间（3分钟）
        const timeout = 180000;
        const timeoutId = setTimeout(() => {
          console.warn(`Tile loading timeout: ${url}`);
          done(new Error("Tile loading timeout"), tile);
        }, timeout);
        const fallbackThreshold = this.options.fallbackThreshold || 1;
        const loadTile = (retryCount = 0) => {
          fetch(url, { mode: "cors", credentials: "omit" })
            .then((response) => {
              if (response.status === 499) {
                this._sh499Count = (this._sh499Count || 0) + 1;
                console.warn(
                  `上海天地图切片 499: ${url}，累计：${this._sh499Count}`
                );
                if (
                  this._sh499Count >= fallbackThreshold &&
                  !this._fallbackTriggered &&
                  this.options.onFallback
                ) {
                  this._fallbackTriggered = true;
                  console.log(
                    `上海天地图 499 达到阈值 ${fallbackThreshold}，触发 fallback`
                  );
                  setTimeout(this.options.onFallback, 1000);
                }
              }
              if (!response.ok) {
                throw new Error("HTTP " + response.status);
              }
              return response.blob();
            })
            .then((blob) => {
              clearTimeout(timeoutId);
              tile.onload = () => done(null, tile);
              tile.onerror = () => done(new Error("Tile load error"), tile);
              tile.src = URL.createObjectURL(blob);
            })
            .catch((err) => {
              console.warn(`Tile loading failed: ${url}`, err);
              if (retryCount < 1) {
                setTimeout(() => {
                  loadTile(retryCount + 1);
                }, 1000); // 1秒后重试
              } else {
                clearTimeout(timeoutId);
                // fetch 失败时回退到直接图片加载，保证尽可能展示
                tile.onload = () => done(null, tile);
                tile.onerror = () =>
                  done(new Error("Tile loading failed"), tile);
                tile.src = url;
              }
            });
        };
        loadTile();
        return tile;
      },
    });
    return new CustomTileLayer(
      shWeixingTile + "/tile/{z}/{y}/{x}" + "?token=" + token,
      Object.assign(
        {
          id: "layer_shTianditu",
          className: "layer_shTianditu",
          tileSize: 256,
          attribution: "",
          Referer: origin,
          crossOrigin: "anonymous",
          zIndex: 3,
          maxZoom: 11,
          minZoom: 2,
          zoom: 6,
          maxNativeZoom: 9,
          fallbackThreshold: 1,
        },
        options
      )
    );
  }

  // 读取 1 小时内持久化的 499 降级状态，命中则直接以谷歌偏移底图初始化
  const fallbackState = getSh499FallbackState();
  const useSatelliteFirst = !!fallbackState;

  const shSatelliteMap = new ShReprojectedSatelliteLayer({
    id: "layer_google_sh",
    className: "layer_google_sh",
    zIndex: 2,
    maxZoom: 22,
    minZoom: 2,
    offsetLat: useSatelliteFirst ? fallbackState.offsetLat || 0 : 0,
    offsetLng: useSatelliteFirst ? fallbackState.offsetLng || 0 : 0,
  });

  const shTianditu = shTiandituLayer(shGisToken, {
    onFallback: () => {},
  });

  return {
    shTianditu,
    shSatelliteMap,
    initialLayer: useSatelliteFirst ? shSatelliteMap : shTianditu,
    setFallback: function (map) {
      shTianditu.options.onFallback = async function () {
        if (map.hasLayer(shSatelliteMap)) return;
        // 根据出现 499 的 tile 坐标计算 GCJ-02 坐标，再转上海偏移得到差值
        const center = map.getCenter();
        const gcjLat = center.lat;
        const gcjLng = center.lng;
        try {
          // about-map-common 的 jsonp 没有错误回调，一旦失败会永久挂起，这里包一层超时
          const shCenter = await Promise.race([
            transformGcj02ToShcrood(gcjLat, gcjLng, jsonp),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error("transformGcj02ToShcrood timeout")),
                5000
              )
            ),
          ]);
          shSatelliteMap.options.offsetLat = shCenter[0] - gcjLat;
          shSatelliteMap.options.offsetLng = shCenter[1] - gcjLng;
        } catch (err) {
          console.warn("获取上海偏移量失败，使用 0 偏移", err);
          shSatelliteMap.options.offsetLat = 0;
          shSatelliteMap.options.offsetLng = 0;
        }
        console.log(
          "上海天地图 499 过多，加载卫星底图备用图层并销毁上海天地图"
        );
        // 持久化 499 标记与偏移量，1 小时内直接用谷歌偏移底图
        saveSh499FallbackState(
          shSatelliteMap.options.offsetLat,
          shSatelliteMap.options.offsetLng
        );
        // addLayer 内部会走 _setView（整数 zoom）触发完整渲染，无需再 redraw
        map.addLayer(shSatelliteMap);
        if (map.hasLayer(shTianditu)) {
          map.removeLayer(shTianditu);
        }
      };
    },
  };
};

export const reversePointfn = (point) => {
  if (!point.length) return [];
  if (point.length && Array.isArray(point[0])) {
    return point.map((points) => reversePointfn(points));
  }
  const [lat, lng] = point;
  if ((lng < 72.004 || lng > 137.8347) && (lat < 0.8293 || lat > 55.8271)) {
    return [lng, lat];
  } else {
    return [lat, lng];
  }
};
